/*
 * Cat & Dog mix pack builder
 * Lets shoppers spread one 2/3/4-tracker pack across any Cat and Dog colours,
 * then switch the Phone System freely. The mix is recorded as a line item property.
 */
(() => {
  if (window.__petTrackerPackBuilderInitialized) return;
  window.__petTrackerPackBuilderInitialized = true;

  const ROOT_SELECTOR =
    'variant-selects[data-product-handle="joylink-smart-pet-tracker-for-dogs-cats"]';
  const BUILDER_SELECTOR = '[data-pet-pack-builder]';
  const SPECIES_ORDER = ['cat', 'dog'];
  const states = new Map();

  const normalize = (value) => String(value || '').trim();
  const getProductInfo = (element) => (element && element.closest ? element.closest('product-info') : null);
  const getSectionKey = (root) => normalize(root && root.dataset ? root.dataset.section : '') || 'main';

  function getState(root) {
    const key = getSectionKey(root);
    if (!states.has(key)) {
      states.set(key, { target: 0, counts: new Map(), phoneSystem: '', previewColor: '' });
    }
    return states.get(key);
  }

  function getSelectedInput(root, optionName) {
    return Array.from(root ? root.querySelectorAll('input[data-option-name]:checked') : []).find(
      (input) => normalize(input.dataset.optionName).toLowerCase() === optionName.toLowerCase()
    );
  }

  function getPackSize(root) {
    const match = normalize(getSelectedInput(root, 'quantity') ? getSelectedInput(root, 'quantity').value : '').match(
      /^([234])\s+tracker\b/i
    );
    return match ? Number(match[1]) : 0;
  }

  function getChoices(root) {
    return Array.from(root.querySelectorAll('[data-pet-pack-choice]'));
  }

  function getChoiceInput(choice) {
    return choice ? choice.querySelector('input[data-option-name="Color"]') : null;
  }

  function isChoiceAvailable(input) {
    return !!(
      input &&
      !input.disabled &&
      !input.classList.contains('disabled') &&
      !input.classList.contains('visually-disabled')
    );
  }

  function getTotal(state) {
    return Array.from(state.counts.values()).reduce((total, count) => total + count, 0);
  }

  function trimToTarget(state, target) {
    let excess = getTotal(state) - target;
    if (excess <= 0) return;
    Array.from(state.counts.keys())
      .reverse()
      .forEach((name) => {
        if (excess <= 0) return;
        const count = state.counts.get(name) || 0;
        const removed = Math.min(count, excess);
        const nextCount = count - removed;
        if (nextCount) state.counts.set(name, nextCount);
        else state.counts.delete(name);
        excess -= removed;
      });
  }

  function syncTarget(state, target) {
    if (!target) {
      state.target = 0;
      state.counts.clear();
      return;
    }
    if (!state.target) state.counts.clear();
    if (state.target !== target) trimToTarget(state, target);
    state.target = target;
  }

  function getChoiceLabel(root, value) {
    const choice = getChoices(root).find((item) => normalize(getChoiceInput(item) ? getChoiceInput(item).value : '') === value);
    return normalize(choice ? choice.dataset.petPackLabel || value : value).replace(/\s+/g, ' ');
  }

  function getPhoneSystem(root, state) {
    const selected = getSelectedInput(root, 'phone system');
    return normalize((state && state.phoneSystem) || (selected ? selected.value : ''));
  }

  function getMixKey(color, phoneSystem) {
    return JSON.stringify([normalize(color), normalize(phoneSystem)]);
  }

  function getMixKeyParts(key) {
    try {
      const parts = JSON.parse(key);
      return Array.isArray(parts) ? parts : [];
    } catch (e) {
      return [];
    }
  }

  function getSummary(root, state) {
    return Array.from(state.counts.entries())
      .filter((entry) => entry[1] > 0)
      .map((entry) => {
        const parts = getMixKeyParts(entry[0]);
        const color = parts[0];
        const phoneSystem = parts[1];
        const base = getChoiceLabel(root, color) + ' x' + entry[1];
        return phoneSystem ? base + ' (Phone System: ' + phoneSystem + ')' : base;
      })
      .join(', ');
  }

  function getPackIncludes(root, state) {
    return Array.from(state.counts.entries())
      .filter((entry) => entry[1] > 0)
      .map((entry) => getChoiceLabel(root, getMixKeyParts(entry[0])[0]) + ' x' + entry[1])
      .join(', ');
  }

  function getSpeciesOf(label) {
    const match = /^(cat|dog)\b/i.exec(normalize(label));
    return match ? match[1].toLowerCase() : '';
  }

  function getSpeciesParts(root, state) {
    const totals = new Map();
    state.counts.forEach((count, key) => {
      const species = getSpeciesOf(getChoiceLabel(root, getMixKeyParts(key)[0]));
      if (species) totals.set(species, (totals.get(species) || 0) + count);
    });
    return SPECIES_ORDER.filter((species) => totals.has(species)).map(
      (species) => species.charAt(0).toUpperCase() + species.slice(1) + ' ' + totals.get(species)
    );
  }

  function getPhoneSystemSummary(root, state, active) {
    if (!active || !state.counts.size) return getPhoneSystem(root, state);
    const totals = new Map();
    state.counts.forEach((count, key) => {
      const phoneSystem = getMixKeyParts(key)[1];
      if (phoneSystem) totals.set(phoneSystem, (totals.get(phoneSystem) || 0) + count);
    });
    if (totals.size === 1) return totals.keys().next().value;
    return Array.from(totals.entries())
      .map((entry) => entry[0] + ' x' + entry[1])
      .join(', ');
  }

  function setSubmitAvailability(root, active, complete) {
    const productInfo = getProductInfo(root);
    const sectionId = productInfo ? productInfo.dataset.section : '';
    if (!productInfo || !sectionId) return;
    const awaitingSelection = active && !complete;
    const submitButton = productInfo.querySelector('#ProductSubmitButton-' + CSS.escape(sectionId));
    const stickyButton = productInfo.querySelector('[data-mobile-sticky-submit]');
    [submitButton, stickyButton].filter(Boolean).forEach((button) => {
      if (awaitingSelection) {
        button.dataset.petPackGate = 'incomplete';
        button.disabled = false;
        button.setAttribute('aria-disabled', 'false');
        const text = button.querySelector('span');
        if (text) text.textContent = (window.variantStrings && window.variantStrings.addToCart) || 'Add to cart';
      } else if (button.dataset.petPackGate) {
        delete button.dataset.petPackGate;
      }
    });
    const dynamicCheckout = productInfo.querySelector('.shopify-payment-button');
    if (!dynamicCheckout) return;
    if (active && !complete && !dynamicCheckout.hidden) {
      dynamicCheckout.dataset.petPackHidden = 'true';
      dynamicCheckout.hidden = true;
    } else if ((!active || complete) && dynamicCheckout.dataset.petPackHidden === 'true') {
      dynamicCheckout.hidden = false;
      delete dynamicCheckout.dataset.petPackHidden;
    }
  }

  function setValidationMessage(root, message) {
    message = message || '';
    const productInfo = getProductInfo(root);
    const wrapper =
      productInfo && productInfo.querySelector('product-form')
        ? productInfo.querySelector('product-form').querySelector('.product-form__error-message-wrapper')
        : null;
    const messageElement = wrapper ? wrapper.querySelector('.product-form__error-message') : null;
    if (!wrapper || !messageElement) return;
    if (message) {
      messageElement.textContent = message;
      wrapper.dataset.petPackError = 'true';
      wrapper.hidden = false;
      return;
    }
    if (wrapper.dataset.petPackError === 'true') {
      messageElement.textContent = '';
      wrapper.hidden = true;
      delete wrapper.dataset.petPackError;
    }
  }

  function getMixChoiceValue(root) {
    const choice = getChoices(root).find((item) => /mixed/i.test(normalize(item ? item.dataset.petPackLabel : '')));
    const input = getChoiceInput(choice);
    return input ? normalize(input.value) : '';
  }

  /* ------------------------------------------------------------------
   * Gallery preview: the "Mixed pack" variants carry no image of their own,
   * so the theme leaves the gallery untouched. We switch the main image
   * ourselves to whatever colour the shopper is currently adding.
   * ------------------------------------------------------------------ */

  function isPackSizeValue(value, size) {
    if (!size) return false;
    return new RegExp('^' + size + '\\s+tracker', 'i').test(normalize(value));
  }

  function getVariantData(root) {
    if (!root) return [];
    if (Array.isArray(root.__petPackVariantData)) return root.__petPackVariantData;
    const script =
      root.querySelector('script[data-tracker-variants]') ||
      (getProductInfo(root) ? getProductInfo(root).querySelector('script[data-tracker-variants]') : null) ||
      document.querySelector('script[data-tracker-variants]');
    let list = [];
    if (script) {
      try {
        const parsed = JSON.parse(script.textContent);
        list = Array.isArray(parsed) ? parsed : parsed.variants || [];
      } catch (e) {
        list = [];
      }
    }
    root.__petPackVariantData = Array.isArray(list) ? list : [];
    return root.__petPackVariantData;
  }

  function getFeaturedImageId(variant) {
    if (!variant) return 0;
    const media = variant.featured_media;
    if (!media) return 0;
    if (typeof media === 'object') return Number(media.id || 0);
    return Number(media || 0);
  }

  function matchesVariantOption(variant, index, value) {
    const options = variant && variant.options ? variant.options : [];
    return normalize(options[index]).toLowerCase() === normalize(value).toLowerCase();
  }

  function pickVariant(root, color, phoneSystem, packSize, requireImage) {
    const variants = getVariantData(root);
    if (!variants.length || !color) return null;
    const byColor = variants.filter(
      (variant) => matchesVariantOption(variant, 0, color) && (!requireImage || getFeaturedImageId(variant))
    );
    if (!byColor.length) return null;
    const bySystem = phoneSystem
      ? byColor.filter((variant) => matchesVariantOption(variant, 2, phoneSystem))
      : byColor;
    const pool = bySystem.length ? bySystem : byColor;
    return (
      pool.find((variant) => isPackSizeValue(variant.options[1], packSize)) ||
      pool.find((variant) => isPackSizeValue(variant.options[1], 1)) ||
      pool[0] ||
      null
    );
  }

  function pickVariantForColor(root, color, phoneSystem, packSize) {
    return pickVariant(root, color, phoneSystem, packSize, true);
  }

  /* The mixed-pack variants have no image, so they must not be used for the gallery,
     but they are exactly the right variant for the SKU / price line. */
  function pickVariantForSku(root, color, phoneSystem, packSize) {
    return pickVariant(root, color, phoneSystem, packSize, false);
  }

  function resolveTargetColor(root, state) {
    const colors = Array.from(state.counts.keys())
      .map((key) => normalize(getMixKeyParts(key)[0]))
      .filter(Boolean);
    if (!colors.length) return '';
    if (new Set(colors).size === 1) return colors[0];
    return getMixChoiceValue(root) || colors[0];
  }

  function syncSkuDisplay(root, state) {
    if (!state.counts.size) return;
    const phoneSystem = getPhoneSystem(root, state);
    if (!phoneSystem) return;
    const productInfo = getProductInfo(root);
    const element = productInfo ? productInfo.querySelector('[id^="Sku-"]') : null;
    if (!element) return;
    const target = resolveTargetColor(root, state);
    const variant = pickVariantForSku(root, target, phoneSystem, getPackSize(root) || 1);
    const sku = normalize(variant && variant.sku);
    if (!sku) return;
    const current = normalize(element.textContent).replace(/^SKU:\s*/i, '');
    if (current === sku) return;
    Array.from(element.childNodes).forEach((node) => {
      if (node.nodeType === 3) node.textContent = '';
    });
    element.appendChild(document.createTextNode(sku));
  }

  function showColorMedia(root, color, phoneSystem, packSize) {
    const productInfo = getProductInfo(root);
    if (!productInfo) return;
    const gallery = productInfo.querySelector('media-gallery');
    if (!gallery || typeof gallery.setActiveMedia !== 'function') return;
    const variant = pickVariantForColor(root, color, phoneSystem, packSize);
    const imageId = getFeaturedImageId(variant);
    if (!imageId) return;
    const sectionId = normalize(productInfo.dataset ? productInfo.dataset.section : '');
    const candidates = sectionId ? [sectionId + '-' + imageId, String(imageId)] : [String(imageId)];
    let mediaId = '';
    candidates.forEach((candidate) => {
      if (!mediaId && gallery.querySelector('[data-media-id="' + candidate + '"]')) mediaId = candidate;
    });
    if (!mediaId) return;
    const activeMedia = gallery.querySelector('.is-active[data-media-id]');
    if (activeMedia && activeMedia.dataset.mediaId === mediaId) return;
    gallery.setActiveMedia(mediaId, false);
  }

  function hasColor(state, color) {
    if (!color) return false;
    return Array.from(state.counts.keys()).some(
      (key) => normalize(getMixKeyParts(key)[0]).toLowerCase() === normalize(color).toLowerCase()
    );
  }

  /* ------------------------------------------------------------------
   * Mobile colour pop-up: on narrow screens the gallery is stacked, so
   * switching the active image is invisible to the shopper. Tapping a
   * colour pops the matching photo; tapping anywhere closes it.
   * ------------------------------------------------------------------ */

  let colorPreviewNode = null;

  function getVariantImageUrl(variant) {
    const media = variant && variant.featured_media;
    if (!media) return '';
    if (media.preview_image) return media.preview_image.src || media.preview_image || '';
    return '';
  }

  function closeColorPreview() {
    const overlay = colorPreviewNode;
    if (!overlay) return;
    overlay.classList.remove('is-open');
    document.body.style.removeProperty('overflow');
    window.setTimeout(() => {
      if (!overlay.classList.contains('is-open')) overlay.hidden = true;
    }, 220);
  }

  function getColorPreview() {
    if (colorPreviewNode && document.body.contains(colorPreviewNode)) return colorPreviewNode;
    const overlay = document.createElement('div');
    overlay.className = 'pet-pack-preview';
    overlay.hidden = true;
    overlay.innerHTML =
      '<div class="pet-pack-preview__panel" role="dialog" aria-modal="true" aria-label="Colour preview">' +
      '<button class="pet-pack-preview__close" type="button" aria-label="Close preview">&times;</button>' +
      '<div class="pet-pack-preview__media"><img alt="" /></div>' +
      '<p class="pet-pack-preview__label"></p>' +
      '<p class="pet-pack-preview__hint">Tap anywhere to close</p>' +
      '</div>';
    document.body.appendChild(overlay);
    overlay.addEventListener('click', (event) => {
      if (event.target.closest('.pet-pack-preview__close') || !event.target.closest('.pet-pack-preview__panel')) {
        closeColorPreview();
      }
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && overlay.classList.contains('is-open')) closeColorPreview();
    });
    colorPreviewNode = overlay;
    return overlay;
  }

  function openColorPreview(root, color, phoneSystem, packSize) {
    if (!window.matchMedia || !window.matchMedia('(max-width: 749px)').matches) return;
    const variant = pickVariantForColor(root, color, phoneSystem, packSize);
    const url = getVariantImageUrl(variant);
    if (!url) return;
    const overlay = getColorPreview();
    const image = overlay.querySelector('img');
    const label = overlay.querySelector('.pet-pack-preview__label');
    const name = getChoiceLabel(root, color);
    if (image.getAttribute('src') !== url) image.setAttribute('src', url);
    image.setAttribute('alt', name);
    label.textContent = name;
    overlay.hidden = false;
    document.body.style.overflow = 'hidden';
    window.requestAnimationFrame(() => overlay.classList.add('is-open'));
  }

  function syncPreviewMedia(root, state) {
    if (!state.counts.size) {
      state.previewColor = '';
      return;
    }
    const phoneSystem = getPhoneSystem(root, state);
    if (!phoneSystem) return;
    const packSize = getPackSize(root) || 1;
    let color = normalize(state.previewColor);
    if (!color || !pickVariantForColor(root, color, phoneSystem, packSize)) {
      const keys = Array.from(state.counts.keys());
      color = keys.length ? normalize(getMixKeyParts(keys[keys.length - 1])[0]) : '';
      state.previewColor = color;
    }
    if (!color) return;
    showColorMedia(root, color, phoneSystem, packSize);
    window.setTimeout(() => showColorMedia(root, color, phoneSystem, packSize), 120);
  }

  /* Keep the submitted variant in sync with the mix:
     1 colour -> that colour's variant, 2+ colours -> the internal "Mixed pack" variant. */
  function applySelection(root, state) {
    if (state.applying) return;
    if (!state.counts.size) return;
    const phoneSystem = getPhoneSystem(root, state);
    if (!phoneSystem) return;
    const target = resolveTargetColor(root, state);
    if (!target) return;
    state.applying = true;
    try {
      syncCarrierVariant(root, target, phoneSystem);
    } finally {
      state.applying = false;
    }
  }

  /* Leaving the pack builder must never leave the internal "Mixed pack" value checked. */
  function resetMixSelection(root) {
    const mixValue = getMixChoiceValue(root);
    if (!mixValue) return;
    const current = getSelectedInput(root, 'Color');
    if (!current || normalize(current.value) !== mixValue) return;
    const fallback = getChoices(root)
      .map(getChoiceInput)
      .find((input) => input && normalize(input.value) !== mixValue && isChoiceAvailable(input));
    if (!fallback) return;
    fallback.checked = true;
    fallback.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function syncCarrierVariant(root, colorValue, phoneSystemValue) {
    getState(root).phoneSystem = phoneSystemValue;
    const colorInput = getChoices(root)
      .map(getChoiceInput)
      .find((candidate) => normalize(candidate ? candidate.value : '') === colorValue && isChoiceAvailable(candidate));
    const phoneSystemInput = Array.from(root.querySelectorAll('input[data-option-name]')).find(
      (candidate) =>
        normalize(candidate.dataset.optionName).toLowerCase() === 'phone system' &&
        normalize(candidate.value) === phoneSystemValue
    );
    if (!colorInput || !phoneSystemInput) return;
    let changedInput = null;
    if (!colorInput.checked) changedInput = colorInput;
    else if (!phoneSystemInput.checked) changedInput = phoneSystemInput;
    colorInput.checked = true;
    phoneSystemInput.checked = true;
    if (changedInput) {
      changedInput.dispatchEvent(new Event('pet-tracker:variant-change', { bubbles: true }));
    }
  }

  function refresh(root) {
    const builder = root.querySelector(BUILDER_SELECTOR);
    if (!builder) return;

    const target = getPackSize(root);
    const active = target > 0;
    const state = getState(root);
    if (!state.phoneSystem) state.phoneSystem = normalize(getSelectedInput(root, 'phone system') ? getSelectedInput(root, 'phone system').value : '');
    syncTarget(state, target);
    if (!active) resetMixSelection(root);

    const total = getTotal(state);
    const complete = active && total === target;
    const property = builder.querySelector('[data-pet-pack-property]');
    const includesProperty = builder.querySelector('[data-pet-pack-includes-property]');
    const phoneSystemProperty = builder.querySelector('[data-pet-phone-system-property]');
    const summary = builder.querySelector('[data-pet-pack-summary]');
    const species = builder.querySelector('[data-pet-pack-species]');
    const phoneSystem = getPhoneSystem(root, state);

    root.classList.toggle('is-building-pet-pack', active);
    builder.hidden = !active;

    builder.querySelectorAll('[data-pet-pack-target]').forEach((item) => {
      item.textContent = String(target || 2);
    });

    getChoices(root).forEach((choice) => {
      const input = getChoiceInput(choice);
      if (!input) return;
      const count = state.counts.get(getMixKey(input.value, phoneSystem)) || 0;
      const addButton = choice.querySelector('[data-pet-pack-add]');
      const removeButton = choice.querySelector('[data-pet-pack-remove]');
      const countElement = choice.querySelector('[data-pet-pack-count]');
      if (addButton) addButton.disabled = !active || !isChoiceAvailable(input) || total >= target;
      if (removeButton) removeButton.disabled = !active || count === 0;
      if (countElement) {
        countElement.hidden = !active || count === 0;
        countElement.textContent = String(count);
      }
      choice.classList.toggle('is-selected', count > 0);
      choice.setAttribute('aria-selected', count > 0 ? 'true' : 'false');
    });

    const totalElement = builder.querySelector('[data-pet-pack-total]');
    if (totalElement) totalElement.textContent = String(total);

    const mixSummary = getSummary(root, state);
    const includesSummary = getPackIncludes(root, state);
    const speciesParts = getSpeciesParts(root, state);

    if (summary) {
      summary.textContent = total
        ? includesSummary + (complete ? '' : ' - add ' + (target - total) + ' more')
        : 'Tap a color to add it.';
    }
    if (species) {
      species.textContent = '';
      speciesParts.forEach((part) => {
        const chip = document.createElement('span');
        chip.textContent = part;
        species.appendChild(chip);
      });
      species.hidden = speciesParts.length === 0;
    }
    if (property) {
      property.value = complete ? mixSummary : '';
      property.disabled = !complete;
    }
    if (includesProperty) {
      includesProperty.value = complete ? includesSummary : '';
      includesProperty.disabled = !complete;
    }
    if (phoneSystemProperty) {
      const phoneSystemSummary = getPhoneSystemSummary(root, state, active);
      phoneSystemProperty.value = phoneSystemSummary;
      phoneSystemProperty.disabled = !phoneSystemSummary;
    }

    if (!active || complete) setValidationMessage(root);
    setSubmitAvailability(root, active, complete);
    syncPreviewMedia(root, state);
    syncSkuDisplay(root, state);
  }

  function changeCount(button, amount) {
    const root = button.closest(ROOT_SELECTOR);
    const choice = button.closest('[data-pet-pack-choice]');
    const input = getChoiceInput(choice);
    const target = getPackSize(root);
    if (!root || !input || !target) return;
    const state = getState(root);
    setValidationMessage(root);
    syncTarget(state, target);
    const name = normalize(input.value);
    const phoneSystem = getPhoneSystem(root, state);
    const mixKey = getMixKey(name, phoneSystem);
    const current = state.counts.get(mixKey) || 0;
    const total = getTotal(state);
    let changed = false;
    if (phoneSystem) {
      if (amount > 0 && total < target) {
        state.counts.set(mixKey, current + 1);
        changed = true;
      } else if (amount < 0 && current > 0) {
        if (current === 1) state.counts.delete(mixKey);
        else state.counts.set(mixKey, current - 1);
        changed = true;
      }
    }
    if (changed) {
      if (amount > 0) state.previewColor = name;
      else if (!(state.counts.get(mixKey) > 0)) state.previewColor = '';
      refresh(root);
      applySelection(root, state);
    }
    refresh(root);
    if (changed && amount > 0) openColorPreview(root, name, phoneSystem, target);
  }

  function previewChoice(choice) {
    const root = choice && choice.closest ? choice.closest(ROOT_SELECTOR) : null;
    const input = getChoiceInput(choice);
    if (!root || !root.classList.contains('is-building-pet-pack') || !isChoiceAvailable(input)) return;
    const state = getState(root);
    const phoneSystem = getPhoneSystem(root, state);
    if (!phoneSystem) return;
    state.previewColor = normalize(input.value);
    applySelection(root, state);
    refresh(root);
    openColorPreview(root, state.previewColor, phoneSystem, getPackSize(root));
  }

  function previewSingleColor(root, input) {
    if (!input || !isChoiceAvailable(input)) return;
    const color = normalize(input.value);
    if (!color || /mixed/i.test(color)) return;
    const state = getState(root);
    const phoneSystem = getPhoneSystem(root, state);
    if (!phoneSystem) return;
    openColorPreview(root, color, phoneSystem, getPackSize(root) || 1);
  }

  document.addEventListener('click', (event) => {
    const addButton = event.target.closest ? event.target.closest('[data-pet-pack-add]') : null;
    const removeButton = event.target.closest ? event.target.closest('[data-pet-pack-remove]') : null;
    const button = addButton || removeButton;
    if (button) {
      event.preventDefault();
      event.stopPropagation();
      changeCount(button, addButton ? 1 : -1);
      return;
    }
    const choice = event.target.closest ? event.target.closest('[data-pet-pack-choice]') : null;
    if (!choice) return;
    const root = choice.closest(ROOT_SELECTOR);
    if (!root) return;
    /* Single tracker: no pack builder, the radio handles the selection.
       We only pop the matching photo so the shopper can see the colour. */
    if (!root.classList.contains('is-building-pet-pack')) {
      previewSingleColor(root, getChoiceInput(choice));
      return;
    }
    event.preventDefault();
    const addControl = choice.querySelector('[data-pet-pack-add]');
    if (addControl && !addControl.disabled) {
      changeCount(addControl, 1);
      return;
    }
    previewChoice(choice);
  });

  document.addEventListener('change', (event) => {
    const root = event.target.closest ? event.target.closest(ROOT_SELECTOR) : null;
    if (!root) return;
    if (normalize(event.target.dataset ? event.target.dataset.optionName : '').toLowerCase() === 'phone system') {
      const state = getState(root);
      const next = normalize(event.target.value);
      if (next && next !== state.phoneSystem) {
        const remapped = new Map();
        state.counts.forEach((count, key) => {
          const color = getMixKeyParts(key)[0];
          const nextKey = getMixKey(color, next);
          remapped.set(nextKey, (remapped.get(nextKey) || 0) + count);
        });
        state.counts = remapped;
        state.phoneSystem = next;
        /* A colour that is not in the pack must not keep driving the preview. */
        if (state.previewColor && !hasColor(state, state.previewColor)) state.previewColor = '';
      }
    }
    window.requestAnimationFrame(() => {
      refresh(root);
      const state = getState(root);
      if (state.counts.size) applySelection(root, state);
    });
  });

  document.addEventListener(
    'submit',
    (event) => {
      const productInfo = event.target.closest ? event.target.closest('product-info') : null;
      const root = productInfo ? productInfo.querySelector(ROOT_SELECTOR) : null;
      if (!root) return;
      refresh(root);
      const target = getPackSize(root);
      if (!target || getTotal(getState(root)) === target) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      refresh(root);
      const builder = root.querySelector(BUILDER_SELECTOR);
      setValidationMessage(root, builder ? builder.dataset.incompleteMessage : '');
    },
    true
  );

  function refreshAll(scope) {
    scope = scope || document;
    if (scope && scope.querySelectorAll) scope.querySelectorAll(ROOT_SELECTOR).forEach(refresh);
  }

  document.addEventListener('DOMContentLoaded', () => refreshAll());
  document.addEventListener('product-info:loaded', (event) => refreshAll(event.target));
  document.addEventListener('shopify:section:load', (event) => refreshAll(event.target));
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      const productInfo = document.querySelector('product-info[data-section="' + CSS.escape(data.sectionId) + '"]');
      window.requestAnimationFrame(() => refreshAll(productInfo || document));
    });
  }
  if (document.readyState !== 'loading') refreshAll();
})();
